import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";

import HeaderBread from "../../../components/HeaderBread";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import Layout from "../../../Layout";
import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
import { getAncillaryPurchaseById } from "../../../utils/ApiCalls/MobileAncillary";

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toUpperCase()) {
    case "SUCCESS":
      return { ...base, background: "#0ab402" };
    case "PAYMENT_FAILED":
    case "FAILED":
      return { ...base, background: "#eb6363" };
    case "BOOKING_FAILED":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const logTypeBadgeStyle = (logType) => {
  const base = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: 500,
    color: "#fff",
    marginRight: "8px",
  };
  switch ((logType || "").toUpperCase()) {
    case "PAYMENT_REQUEST":
      return { ...base, background: "#1976d2" };
    case "PAYMENT_RESPONSE":
      return { ...base, background: "#7b1fa2" };
    case "SEAWARE_REQUEST":
      return { ...base, background: "#f57c00" };
    case "SEAWARE_RESPONSE":
      return { ...base, background: "#388e3c" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const itemStatusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toUpperCase()) {
    case "SUCCESS":
      return { ...base, background: "#0ab402" };
    case "FAILED":
      return { ...base, background: "#eb6363" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const fieldStyle = {
  display: "flex",
  gap: "8px",
  marginBottom: "10px",
  alignItems: "flex-start",
};

const labelStyle = {
  fontWeight: 600,
  minWidth: "160px",
  color: "#555",
  fontSize: "13px",
};

const valueStyle = {
  fontSize: "13px",
  color: "#222",
};

const ViewPurchase = () => {
  const { id } = useParams();
  const [, dispatch] = useDataLayerValue();
  const [purchase, setPurchase] = useState(null);

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Mobile Ancillary", route: "/shore/mobile-ancillary/purchases" },
    { title: "Ancillary Purchases", route: "/shore/mobile-ancillary/purchases" },
    { title: "View", route: "#" },
  ];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    const res = await getAncillaryPurchaseById(id);
    if (res.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: res.error, type: "error" },
      });
    } else {
      setPurchase(res.data || res);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  if (!purchase) return null;

  const items = purchase.items || [];
  const auditLogs = purchase.logs || [];

  const itemColumns = [
    { key: "ancillaryCode", title: "Ancillary Code" },
    { key: "ancillaryName", title: "Ancillary Name" },
    { key: "guestName", title: "Guest Name" },
    { key: "cabinNo", title: "Cabin No" },
    { key: "addonName", title: "Addon Name" },
    { key: "price", title: "Price" },
  ];

  return (
    <Layout type={"read_per"} module_data={"Ancillary Purchases"}>
      <CustomHeaderTitle title={"View Ancillary Purchase"} />
      <div>
        <HeaderBread navigation={navigation} title={"View Ancillary Purchase"} />
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">

            {/* Purchase Summary */}
            <div className="card" style={{ marginBottom: "20px" }}>
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Purchase Summary</h5>
              </div>
              <div className="card-body">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px 20px",
                  }}
                >
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Booking ID:</span>
                    <span style={valueStyle}>{purchase.bookingId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Transaction ID:</span>
                    <span style={valueStyle}>{purchase.transactionId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Seaware Transaction ID:</span>
                    <span style={valueStyle}>{purchase.seawareTransactionId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Reference No:</span>
                    <span style={valueStyle}>{purchase.referenceNo || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Total Amount:</span>
                    <span style={valueStyle}>
                      {purchase.currency}{" "}
                      {purchase.totalAmount != null
                        ? Number(purchase.totalAmount).toFixed(2)
                        : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Status:</span>
                    <span>
                      <span style={statusChipStyle(purchase.displayStatus)}>
                        {purchase.displayStatus || "PENDING"}
                      </span>
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Card Type:</span>
                    <span style={valueStyle}>{purchase.cardType || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Card Number:</span>
                    <span style={valueStyle}>{purchase.cardNo || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Expiry Date:</span>
                    <span style={valueStyle}>{purchase.expiryDate || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>MID:</span>
                    <span style={valueStyle}>{purchase.mid || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Created At:</span>
                    <span style={valueStyle}>
                      {purchase.createdAt
                        ? moment(purchase.createdAt).format("DD-MM-YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="card" style={{ marginBottom: "20px" }}>
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Ancillary Items</h5>
              </div>
              <div className="card-body" style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    minWidth: "1100px",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      {itemColumns.map((col) => (
                        <th
                          key={col.key}
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            borderBottom: "1px solid #e0e0e0",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {col.title}
                        </th>
                      ))}
                      <th
                        style={{
                          padding: "8px 12px",
                          textAlign: "left",
                          borderBottom: "1px solid #e0e0e0",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "8px 12px",
                          textAlign: "left",
                          borderBottom: "1px solid #e0e0e0",
                          fontWeight: 600,
                          minWidth: "320px",
                        }}
                      >
                        Error Message
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={itemColumns.length + 2}
                          style={{
                            padding: "16px 12px",
                            textAlign: "center",
                            color: "#999",
                          }}
                        >
                          No items found
                        </td>
                      </tr>
                    ) : (
                      items.map((item, idx) => (
                        <tr
                          key={idx}
                          style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                        >
                          {itemColumns.map((col) => (
                            <td
                              key={col.key}
                              style={{
                                padding: "8px 12px",
                                borderBottom: "1px solid #f0f0f0",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item[col.key] != null ? String(item[col.key]) : "-"}
                            </td>
                          ))}
                          <td
                            style={{
                              padding: "8px 12px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <span style={itemStatusChipStyle(item.status)}>
                              {item.status || "-"}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "8px 12px",
                              borderBottom: "1px solid #f0f0f0",
                              minWidth: "320px",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.errorMessage || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Audit Logs */}
            <div className="card">
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Audit Logs</h5>
              </div>
              <div className="card-body">
                {auditLogs.length === 0 ? (
                  <p style={{ color: "#999", fontSize: "13px" }}>No audit logs found.</p>
                ) : (
                  auditLogs.map((log, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: "16px",
                        borderLeft: "3px solid #e0e0e0",
                        paddingLeft: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                          gap: "8px",
                        }}
                      >
                        <span style={logTypeBadgeStyle(log.logType)}>
                          {log.logType || "LOG"}
                        </span>
                        <span style={{ fontSize: "12px", color: "#888" }}>
                          {log.createdAt
                            ? moment(log.createdAt).format("DD-MM-YYYY HH:mm:ss")
                            : ""}
                        </span>
                      </div>
                      <pre
                        style={{
                          background: "#f5f5f5",
                          padding: "10px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          overflowX: "auto",
                          overflowY: "auto",
                          margin: 0,
                          maxHeight: "200px",
                        }}
                      >
                        {(() => {
                          try {
                            const parsed =
                              typeof log.payload === "string"
                                ? JSON.parse(log.payload)
                                : log.payload;
                            return JSON.stringify(parsed, null, 2);
                          } catch {
                            return String(log.payload || "");
                          }
                        })()}
                      </pre>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ViewPurchase;
